<template>
	<BaseLayout :footer-full="true">
		<div class="container">
			<h1 class="heading-1 text-align-center mb-100 mb-200-md">Fundrise IRA Fee Payment Program</h1>
			<section class="grid-md">
				<article class="col-8-md col-offset-2-md position-relative">
					<div class="module">
						<div class="module-header">
							<h2 class="heading-5">Terms and conditions</h2>
						</div>
						<div class="module-section">
							<p>
								The Fundrise IRA Fee Payment Program allows individuals who invest through an IRA to
								receive an “IRA fee waiver” (as defined below) for each account designated by Fundrise
								as having qualified for the Fundrise IRA Fee Payment Program. Subject to the limitations
								outlined in these terms and conditions, if an investor receives an “IRA fee waiver” it
								means that Fundrise Advisors, LLC has agreed to pay, on behalf of such investor, Inspira
								Financial’s annual investment custody fee related to the investor's Fundrise assets, due
								to the investor's Fundrise assets having met the qualifications below, as determined by
								Fundrise Advisors, LLC.
							</p>
							<p>
								To qualify for the Fundrise IRA Fee Payment Program, an individual must either (i)
								invest a minimum of {{ yearlyOneTimeThreshold }} in Fundrise assets through the IRA by
								the initial cutoff date, and annually thereafter, or (ii) have at least
								{{ annualRecurringThreshold }} invested in Fundrise assets through the IRA at the
								initial cutoff date, and annually thereafter (regardless of whether these accounts
								invest additional funds in any given year).
							</p>
							<p>
								The cutoff date to qualify for Fundrise to pay, on behalf of the individual, Inspira
								Financial’s annual investment custody fee for the first year and subsequent years while
								an individual maintains the investment account will be the fifth day of the month two
								months after the month the account was opened. For example, if an individual opens an
								IRA in January, the individual must invest at least {{ currency(3000, 0) }} in the
								Fundrise assets through the IRA by March 5th to get the first year of Inspira
								Financial’s fees paid for by Fundrise.
							</p>
							<h3 class="heading-6 mt-100">Additional fees</h3>
							<p>
								The Fundrise IRA Fee Payment Program only applies to the annual investment custody fee
								charged by Inspira Financial Trust, LLC and does not include any fees and expenses
								charged by Fundrise Advisors, LLC or its affiliates to the individual funds available on
								the Fundrise Platform and held in your Fundrise Account (e.g., the eREITs and eFunds).
								In some cases, Inspira Financial may charge additional IRA account fees that are not
								directly related to the Fundrise assets. See the
								<BaseCtaLink :link="{ href: mtcFeesUrl, text: 'full fee schedule' }" />.
							</p>
							<h3 class="heading-6 mt-100">Program criteria</h3>
							<p>
								To qualify for the Fundrise IRA Fee Payment Program using the annual investment
								threshold of {{ yearlyOneTimeThreshold }}, an individual must place investments of at
								least {{ yearlyOneTimeThreshold }} on the Fundrise platform through the IRA.
							</p>
							<p>
								To qualify for the Fundrise IRA Fee Payment Program by having at least
								{{ annualRecurringThreshold }} invested in Fundrise assets through the IRA, either (i)
								the cost of shares owned through the IRA plus the amount of any pending investments, or
								(ii) the estimated value of shares owned through the IRA plus the amount of any pending
								investments, must be greater than {{ annualRecurringThreshold }}.
							</p>
							<p>
								Contributions are limited according to the standard IRS contribution limits. This limit
								applies across all IRAs in the individual’s name. For investments funded via rollover,
								the associated investment is not placed until the rollover has completed, at which time
								the rollover amount will be included in the amount needed to qualify for the Fundrise
								IRA Fee Payment Program.
							</p>
							<p>
								If an individual has multiple IRAs with Inspira Financial, each of which holds Fundrise
								assets, the qualifications for the Fundrise IRA Fee Payment Program will apply to each
								account separately. The Fundrise IRA Fee Payment Program is only available for
								Traditional and Roth IRAs.
							</p>
							<p>
								Fundrise Advisors, LLC reserves the right to terminate this offer at any time for any
								reason, and to refuse or recover any IRA fee waiver if Fundrise Advisors, LLC determines
								that it was obtained under wrongful or fraudulent circumstances, that any rules or
								regulations would be violated, or that any terms of the Fundrise Client Agreement have
								been violated.
							</p>
						</div>
					</div>
				</article>
			</section>
		</div>
	</BaseLayout>
</template>

<script setup lang="ts">
import { annualRecurringThreshold, yearlyOneTimeThreshold } from '@constants/ira';
import { currency } from '@filters/shared-filters';
import { useHead } from '@unhead/vue';

const mtcFeesUrl = 'https://www.mtrustcompany.com/sites/default/files/forms/static/Self_Directed_IRA_Fee_Schedule.pdf';

function setup(): void {
	useHead({
		title: 'Fundrise IRA Fee Payment Program Terms and Conditions',
		meta: [
			{ property: 'og:title', content: 'Fundrise IRA Fee Payment Program Terms and Conditions' },
			{
				name: 'description',
				content: 'Fundrise IRA Fee Payment Program Terms and Conditions'
			},
			{
				property: 'og:description',
				content: 'Fundrise IRA Fee Payment Program Terms and Conditions'
			}
		]
	});
}
setup();
</script>
